import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Loader2, Inbox, Search, ChevronDown, Check, X, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

type InterestStatus = 'new' | 'reviewing' | 'accepted' | 'invited' | 'rejected';

const STATUS_LABELS: Record<InterestStatus, string> = {
  new: 'Ny',
  reviewing: 'Granskas',
  accepted: 'Godkänd',
  invited: 'Inbjuden',
  rejected: 'Avslagen',
};

const STATUS_VARIANTS: Record<InterestStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  new: 'default',
  reviewing: 'secondary',
  accepted: 'outline',
  invited: 'outline',
  rejected: 'destructive',
};

interface InvitationInfo {
  status: string;
  consumed_at: string | null;
  created_at: string;
  email: string;
}

const FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Alla' },
  { value: 'new', label: 'Nya' },
  { value: 'reviewing', label: 'Granskas' },
  { value: 'accepted', label: 'Godkända' },
  { value: 'invited', label: 'Inbjudna' },
  { value: 'rejected', label: 'Avslagna' },
];

interface InterestRow {
  id: string;
  name: string;
  region: string | null;
  email: string;
  message: string | null;
  created_at: string;
  status: string;
  user_id: string | null;
  producer_id: string | null;
}

export default function AdminProducerInterestPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  // Accept dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<InterestRow | null>(null);

  // Place strategy state
  const [placeMode, setPlaceMode] = useState<'create' | 'link'>('create');
  const [placeName, setPlaceName] = useState('');
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [placeSearchQuery, setPlaceSearchQuery] = useState('');

  // Fetch interest list
  const { data: interests = [], isLoading } = useQuery({
    queryKey: ['admin-producer-interest', filter],
    queryFn: async () => {
      let query = supabase
        .from('producer_interest')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter === 'rejected') {
        query = query.in('status', ['rejected', 'invited']);
      } else if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as InterestRow[];
    },
  });

  // Fetch invitations to resolve accepted status
  const { data: invitations = [] } = useQuery({
    queryKey: ['admin-producer-invitations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('producer_invitations')
        .select('status, consumed_at, created_at, email')
        .in('status', ['pending', 'accepted', 'declined']);
      if (error) throw error;
      return (data ?? []) as InvitationInfo[];
    },
  });

  // Build email → invitation lookup
  const invitationByEmail = new Map<string, InvitationInfo>();
  for (const inv of invitations) {
    const existing = invitationByEmail.get(inv.email);
    if (!existing || inv.status === 'accepted' || (existing.status !== 'accepted' && inv.created_at > existing.created_at)) {
      invitationByEmail.set(inv.email, inv);
    }
  }

  // Status update mutation
  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('producer_interest')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-producer-interest'] });
    },
  });

  // Place search query
  const { data: placeResults = [], isFetching: isSearchingPlaces } = useQuery({
    queryKey: ['admin-place-search', placeSearchQuery],
    queryFn: async () => {
      if (placeSearchQuery.trim().length < 2) return [];
      const { data, error } = await supabase
        .from('places')
        .select('id, name, municipality, county, status')
        .ilike('name', `%${placeSearchQuery.trim()}%`)
        .order('name')
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
    enabled: placeSearchQuery.trim().length >= 2 && placeMode === 'link',
  });

  // Count producers already linked to selected place
  const { data: linkedProducers } = useQuery({
    queryKey: ['admin-place-linked-producers', selectedPlaceId],
    queryFn: async () => {
      if (!selectedPlaceId) return null;
      const { data, error } = await supabase
        .from('producers')
        .select('id, name')
        .eq('place_id', selectedPlaceId)
        .in('status', ['public', 'draft'])
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!selectedPlaceId && placeMode === 'link',
  });

  // Accept mutation — calls RPC that atomically creates producer + place (+ member if user_id present)
  const acceptInterest = useMutation({
    mutationFn: async (params: {
      interest_id: string;
      place_mode: 'create' | 'link';
      place_name?: string;
      place_id?: string;
    }) => {
      const { data, error } = await supabase.rpc('admin_accept_producer_interest', {
        p_interest_id: params.interest_id,
        p_place_mode: params.place_mode,
        p_place_name: params.place_name ?? null,
        p_place_id: params.place_id ?? null,
      } as any);
      if (error) {
        throw new Error(error.message || 'Kunde inte godkänna ansökan');
      }
      return data as { producer_id: string; place_id: string; created_place: boolean; has_user: boolean };
    },
    onSuccess: (_data) => {
      setInviteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin-producer-interest'] });
      if (_data.has_user) {
        toast.success('Värd godkänd — producent, plats och medlemskap skapades');
      } else {
        toast.success('Värd godkänd — producent och plats skapades. Skicka inbjudan för att koppla användaren.');
      }
    },
    onError: (err: Error) => {
      console.error('Accept error:', err);
      toast.error(err.message || 'Kunde inte godkänna ansökan');
    },
  });

  // Send invite mutation — uses existing create_producer_invite RPC
  const sendInvite = useMutation({
    mutationFn: async ({ producerId, email }: { producerId: string; email: string }) => {
      const { data, error } = await supabase.rpc('create_producer_invite', {
        p_producer_id: producerId,
        p_email: email,
        p_role: 'admin',
      } as any);
      if (error) {
        throw new Error(error.message || 'Kunde inte skicka inbjudan');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-producer-interest'] });
      queryClient.invalidateQueries({ queryKey: ['admin-producer-invitations'] });
      toast.success('Inbjudan skickad');
    },
    onError: (err: Error) => {
      console.error('Invite error:', err);
      if (err.message.includes('active invitation already exists')) {
        toast.error('En aktiv inbjudan finns redan för denna e-postadress');
      } else {
        toast.error(err.message || 'Kunde inte skicka inbjudan');
      }
    },
  });

  const openInviteDialog = (row: InterestRow) => {
    setInviteTarget(row);
    setPlaceMode('create');
    setPlaceName(row.name || '');
    setSelectedPlaceId(null);
    setPlaceSearchQuery('');
    setInviteOpen(true);
  };

  const isAcceptValid = useMemo(() => {
    if (placeMode === 'create') return placeName.trim().length >= 2;
    if (placeMode === 'link') return !!selectedPlaceId;
    return false;
  }, [placeMode, placeName, selectedPlaceId]);

  const handleAccept = () => {
    if (!inviteTarget || !isAcceptValid) return;
    acceptInterest.mutate({
      interest_id: inviteTarget.id,
      place_mode: placeMode,
      place_name: placeMode === 'create' ? placeName.trim() : undefined,
      place_id: placeMode === 'link' ? selectedPlaceId! : undefined,
    });
  };

  const handleReject = (id: string) => {
    updateStatus.mutate({ id, status: 'rejected' });
    toast.success('Ansökan avslagen');
  };

  const handleSendInvite = (row: InterestRow) => {
    if (!row.producer_id) {
      toast.error('Producent saknas — godkänn ansökan först');
      return;
    }
    sendInvite.mutate({ producerId: row.producer_id, email: row.email });
  };

  // When filter is 'rejected', also include rows whose invitation is declined
  const effectiveInterests = filter === 'rejected'
    ? (() => {
        const rejectedIds = new Set(interests.map((r) => r.id));
        return interests;
      })()
    : interests;

  // Client-side search filter
  const filtered = effectiveInterests.filter((row) => {
    if (filter === 'rejected') {
      const inv = invitationByEmail.get(row.email);
      const isDeclined = inv?.status === 'declined';
      if (row.status !== 'rejected' && !isDeclined) return false;
    }

    if (!search) return true;
    const q = search.toLowerCase();
    return (
      row.name.toLowerCase().includes(q) ||
      row.email.toLowerCase().includes(q) ||
      (row.region?.toLowerCase().includes(q) ?? false) ||
      (row.message?.toLowerCase().includes(q) ?? false)
    );
  });

  const isAnon = inviteTarget && !inviteTarget.user_id;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Ansökningar</h1>
          <p className="text-muted-foreground mt-1">
            Inkomna intresseanmälningar från värdar.
          </p>
        </div>

        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Sök namn, e-post, region..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {FILTER_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={filter === opt.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {isLoading ? (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
            <p className="text-muted-foreground mt-4">Laddar ansökningar...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Inbox className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {search || filter !== 'all' ? 'Inga ansökningar matchar' : 'Inga ansökningar ännu'}
            </h3>
            <p className="text-muted-foreground">
              {search || filter !== 'all'
                ? 'Prova att ändra filter eller sökning.'
                : 'Intresseanmälningar visas här när de kommer in.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((row) => (
              <InterestCard
                key={row.id}
                row={row}
                invitation={invitationByEmail.get(row.email)}
                onInvite={() => openInviteDialog(row)}
                onReject={() => handleReject(row.id)}
                onReview={() => updateStatus.mutate({ id: row.id, status: 'reviewing' })}
                onSendInvite={() => handleSendInvite(row)}
                isSendingInvite={sendInvite.isPending}
              />
            ))}
            <p className="text-sm text-muted-foreground text-center pt-2">
              Visar {filtered.length} ansökning{filtered.length !== 1 ? 'ar' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Accept dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-background sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Godkänn ansökan</DialogTitle>
            <DialogDescription>
              {isAnon
                ? `Skapar producent och plats för ${inviteTarget?.name}. Du skickar inbjudan efteråt.`
                : `Skapar producent och medlemskap för ${inviteTarget?.name}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Anon notice */}
            {isAnon && (
              <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                Ansökan skickades utan inloggning. Medlemskap skapas när mottagaren accepterar inbjudan.
              </div>
            )}

            {/* Info */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <Label className="text-muted-foreground text-xs">Namn</Label>
                <p className="text-foreground">{inviteTarget?.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">E-post</Label>
                <p className="text-foreground">{inviteTarget?.email}</p>
              </div>
              {inviteTarget?.region && (
                <div>
                  <Label className="text-muted-foreground text-xs">Region</Label>
                  <p className="text-foreground">{inviteTarget.region}</p>
                </div>
              )}
            </div>

            {/* Place strategy */}
            <div className="space-y-3 border-t pt-4">
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                Plats
              </Label>

              <RadioGroup
                value={placeMode}
                onValueChange={(v) => {
                  setPlaceMode(v as 'create' | 'link');
                  setSelectedPlaceId(null);
                }}
                className="gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="create" id="place-create" />
                  <Label htmlFor="place-create" className="font-normal cursor-pointer">
                    Skapa ny plats (utkast)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="link" id="place-link" />
                  <Label htmlFor="place-link" className="font-normal cursor-pointer">
                    Koppla till befintlig plats
                  </Label>
                </div>
              </RadioGroup>

              {/* Create mode: place name */}
              {placeMode === 'create' && (
                <div className="space-y-1.5">
                  <Label htmlFor="place-name" className="text-xs text-muted-foreground">
                    Platsnamn
                  </Label>
                  <Input
                    id="place-name"
                    value={placeName}
                    onChange={(e) => setPlaceName(e.target.value)}
                    placeholder="Ange platsnamn..."
                  />
                  {placeName.trim().length > 0 && placeName.trim().length < 2 && (
                    <p className="text-xs text-destructive">Minst 2 tecken</p>
                  )}
                </div>
              )}

              {/* Link mode: place search */}
              {placeMode === 'link' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={placeSearchQuery}
                      onChange={(e) => {
                        setPlaceSearchQuery(e.target.value);
                        setSelectedPlaceId(null);
                      }}
                      placeholder="Sök plats..."
                      className="pl-8 h-9 text-sm"
                    />
                  </div>

                  {isSearchingPlaces && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Söker...
                    </div>
                  )}

                  {placeSearchQuery.trim().length >= 2 && !isSearchingPlaces && placeResults.length === 0 && (
                    <p className="text-xs text-muted-foreground py-2">Inga platser hittades</p>
                  )}

                  {placeResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                      {placeResults.map((place) => (
                        <button
                          key={place.id}
                          type="button"
                          onClick={() => setSelectedPlaceId(place.id)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors flex items-center justify-between gap-2 ${
                            selectedPlaceId === place.id ? 'bg-muted' : ''
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">{place.name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {[place.municipality, place.county].filter(Boolean).join(', ') || '—'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge variant="outline" className="text-[10px] px-1.5">
                              {place.status}
                            </Badge>
                            {selectedPlaceId === place.id && (
                              <Check className="h-3.5 w-3.5 text-primary" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {!selectedPlaceId && placeSearchQuery.trim().length < 2 && (
                    <p className="text-xs text-muted-foreground">Skriv minst 2 tecken för att söka</p>
                  )}

                  {/* Show linked producers info */}
                  {selectedPlaceId && linkedProducers && linkedProducers.length > 0 && (
                    <p className="text-xs text-muted-foreground bg-muted/50 rounded px-3 py-2">
                      Redan kopplade verksamheter: {linkedProducers.length}
                      {linkedProducers.length <= 2 && (
                        <span> — {linkedProducers.map(p => p.name).join(', ')}</span>
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Validation hint */}
              {!isAcceptValid && (
                <p className="text-xs text-muted-foreground">
                  {placeMode === 'create'
                    ? 'Ange ett platsnamn för att kunna godkänna.'
                    : 'Välj en plats i listan för att kunna godkänna.'}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={handleAccept}
              disabled={acceptInterest.isPending || !isAcceptValid}
            >
              {acceptInterest.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Godkänner...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Godkänn
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

/* ──── Interest card ──── */

function InterestCard({
  row,
  invitation,
  onInvite,
  onReject,
  onReview,
  onSendInvite,
  isSendingInvite,
}: {
  row: InterestRow;
  invitation?: InvitationInfo;
  onInvite: () => void;
  onReject: () => void;
  onReview: () => void;
  onSendInvite: () => void;
  isSendingInvite: boolean;
}) {
  const status = row.status as InterestStatus;
  const isActionable = status === 'new' || status === 'reviewing';

  // Accepted but no user → needs invite
  const needsInvite = status === 'accepted' && !row.user_id && !!row.producer_id;

  // Resolve display badge: invitation status overrides interest status
  const invStatus = invitation?.status;
  const isAccepted = status === 'accepted' || (status === 'invited' && invStatus === 'accepted');
  const isDeclined = invStatus === 'declined';

  const { badgeLabel, badgeVariant } = needsInvite
    ? { badgeLabel: 'Väntar på inbjudan', badgeVariant: 'secondary' as const }
    : isAccepted
      ? { badgeLabel: 'Godkänd', badgeVariant: 'secondary' as const }
      : isDeclined
        ? { badgeLabel: 'Avböjd', badgeVariant: 'destructive' as const }
        : { badgeLabel: STATUS_LABELS[status] ?? status, badgeVariant: STATUS_VARIANTS[status] ?? 'secondary' as const };

  const displayDate = row.created_at;
  const timePrefix = isAccepted ? 'Godkänd' : isDeclined ? 'Avböjd' : status === 'invited' ? 'Inbjuden' : '';

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-foreground">{row.name}</h3>
            <Badge variant={badgeVariant}>
              {badgeLabel}
            </Badge>
            {!row.user_id && (
              <Badge variant="outline" className="text-[10px]">Anonym</Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
            <span>{row.email}</span>
            {row.region && <span>{row.region}</span>}
            <span>
              {timePrefix ? `${timePrefix} • ` : ''}
              {formatDistanceToNow(new Date(displayDate), { addSuffix: true, locale: sv })}
            </span>
          </div>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          {/* Actionable: new/reviewing */}
          {isActionable && (
            <>
              {status === 'new' && (
                <Button variant="outline" size="sm" onClick={onReview}>
                  Granska
                </Button>
              )}
              <Button size="sm" onClick={onInvite}>
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Godkänn
              </Button>
              <Button variant="ghost" size="sm" onClick={onReject}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </>
          )}

          {/* Accepted but needs invite */}
          {needsInvite && (
            <Button size="sm" onClick={onSendInvite} disabled={isSendingInvite}>
              {isSendingInvite ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5 mr-1.5" />
              )}
              Skicka inbjudan
            </Button>
          )}
        </div>
      </div>

      {row.message && (
        <Collapsible>
          <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ChevronDown className="h-3.5 w-3.5" />
            Meddelande
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <p className="text-sm text-muted-foreground bg-muted/50 rounded p-3 whitespace-pre-wrap">
              {row.message}
            </p>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
